import React from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Sparkles } from "lucide-react";
import CategoryMedia from "./CategoryMedia";

/**
 * Desktop "shop by category" landing grid. Shown when no parent category is selected.
 * Mirrors the mobile landing but uses a 4-col grid for desktop breathing room.
 */
const DesktopCategoryLanding = ({ parents, onSelect, onDisabledClick }) => {
    const { t } = useI18n();
    const labelOf = (p) => {
        const tr = t(p.name_key);
        return tr && tr !== p.name_key ? tr : p.label;
    };
    // Hide empty parents entirely (user spec).
    parents = parents.filter((p) => !p.disabled);

    return (
        <section data-testid="desktop-category-landing">
            <div className="flex items-center gap-2.5 mb-2">
                <span className="h-9 w-9 rounded-xl bg-[#FF5722] text-white flex items-center justify-center">
                    <Sparkles className="h-5 w-5" />
                </span>
                <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
                    {t("section_shop_by_category")}
                </h2>
            </div>
            <p className="text-sm text-neutral-500 mb-6">
                {t("section_select_category")}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {parents.map((p) => {
                    const label = labelOf(p);
                    if (p.disabled) {
                        return (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => onDisabledClick(label)}
                                aria-disabled="true"
                                data-testid={`desktop-parent-${p.id}-disabled`}
                                className="group relative bg-neutral-50 border border-dashed border-neutral-300 rounded-2xl p-5 flex flex-col items-start gap-3 text-left opacity-70 cursor-not-allowed hover:opacity-100 transition-opacity"
                            >
                                <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider bg-neutral-900 text-white px-1.5 py-0.5 rounded">
                                    {t("category_coming_soon")}
                                </span>
                                <CategoryMedia parent={p} size="h-16 w-16" grayscale />
                                <div>
                                    <div className="font-semibold text-sm text-neutral-500 leading-tight">
                                        {label}
                                    </div>
                                    <div className="text-[11px] text-neutral-400 mt-1">
                                        {t("category_coming_soon")}
                                    </div>
                                </div>
                            </button>
                        );
                    }
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => onSelect(p.id)}
                            data-testid={`desktop-parent-${p.id}`}
                            className="group bg-white border border-neutral-200 rounded-2xl p-2.5 flex flex-col gap-2 hover:border-[#FF5722] hover:shadow-lg hover:-translate-y-0.5 transition-all"
                        >
                            <CategoryMedia parent={p} size="aspect-square w-full" className="group-hover:scale-[1.03] transition-transform" />
                            <div className="font-semibold text-sm text-neutral-900 leading-tight text-center px-1 pb-1 mt-auto line-clamp-2">
                                {label}
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

export default DesktopCategoryLanding;
